import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { company } from '~/company/Company';
import type { ProductViewProps } from '~/pages/Product/Product';
import { buildBadgeColorStyle } from '~/utils/color';

import styles from './ProductBadges.module.scss';

const ProductBadges = ({
  currentItem,
  isExpress,
  isBundle,
  className,
  isLoading,
}: {
  className?: string;
} & Pick<
  ProductViewProps,
  'currentItem' | 'isExpress' | 'isBundle' | 'isLoading'
>) => {
  const { t } = useTranslation();

  /**
   * Unlike product cards, the PDP has room to wrap and shows every badge —
   * the three-badge cap in BRD JS-14286 applies to cards only.
   */
  const items = (() => {
    const result: {
      key: string;
      type: string;
      text: string;
      backgroundColor?: Nullable<string>;
      textColor?: Nullable<string>;
    }[] = [];

    // Bundles borrow their offers' badges through the shared parsing path,
    // but the CMS cannot assign badges to a bundle itself — showing the
    // borrowed ones was agreed off until it can.
    if (!isBundle) {
      for (const badge of currentItem?.catalogBadges ?? []) {
        result.push({
          key: `catalog-${badge.id}`,
          type: 'catalog',
          text: badge.name,
          backgroundColor: badge.backgroundColor,
          textColor: badge.textColor,
        });
      }
    }

    if (currentItem?.discountPercentage) {
      result.push({
        key: 'discount',
        type: 'discount',
        text: currentItem.discountPercentage,
      });
    }

    if (isExpress) {
      result.push({ key: 'express', type: 'express', text: t('express') });
    }

    if (isBundle) {
      result.push({ key: 'bundle', type: 'bundle', text: t('bundle') });
    }

    return result;
  })();

  const rootClassName = classes([styles.root, className]);

  if (isLoading) {
    return (
      <div className={rootClassName}>
        <div className={styles.inner}>
          <Skeleton containerClassName={styles.skeleton} />
          <Skeleton containerClassName={styles.skeleton} />
          {!company.is('mcosmetic') && (
            <Skeleton containerClassName={styles.skeleton} />
          )}
        </div>
      </div>
    );
  }
  if (!items.length) {
    return null;
  }
  return (
    <div className={rootClassName}>
      {items.map(({ key, type, text, backgroundColor, textColor }) => (
        <span
          key={key}
          className={styles.badge}
          data-badge-type={type}
          style={buildBadgeColorStyle(backgroundColor, textColor)}
        >
          {text}
        </span>
      ))}
    </div>
  );
};

export default observer(ProductBadges);
